import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getQuoteLinkDetail, saveQuoteLinkDraft, submitQuoteLink } from './quoteLinkApi';
import { VpSpinner, VpConfirmModal, money } from '../vendorPortal/components/VpUI';
import VpIcon from '../vendorPortal/components/VpIcon';
import '../vendorPortal/vendorPortal.scss';
// @ts-ignore
import neelsLogo from '../assets/logos/neels_logo.png';

interface Line {
	productId: string;
	name: string;
	unit: string;
	quantity: number;
	product_description: string;
	usage_note: string;
	rate: number | '';
	tax: number | '';
	discount: number | '';
	discountType: 'percentage' | 'fixed';
}

const lineTotal = (l: Line) => {
	const qty = Number(l.quantity) || 0;
	const rate = Number(l.rate) || 0;
	const disc = Number(l.discount) || 0;
	const tax = Number(l.tax) || 0;
	const afterDisc =
		l.discountType === 'fixed' ? qty * (rate - disc) : qty * (rate - (rate * disc) / 100);
	return afterDisc + (afterDisc * tax) / 100;
};

// Map a product row (requisition product, optionally with a saved quote rate).
const mapLine = (p: any, quoted?: any): Line => ({
	productId: p.productId || p.productInfo?._id,
	name: p.productInfo?.name || p.product_description || 'Item',
	unit: p.productInfo?.unit?.name || p.productInfo?.unitName || '',
	quantity: p.quantity || 0,
	product_description: p.product_description || '',
	usage_note: p.usage_note || '',
	rate: quoted?.rate ?? '',
	tax: quoted?.tax ?? '',
	discount: quoted?.discount ?? '',
	discountType: quoted?.discountType || 'percentage',
});

const QuoteLinkPage = () => {
	const { token } = useParams();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [vendor, setVendor] = useState<any>(null);
	const [req, setReq] = useState<any>(null);
	const [lines, setLines] = useState<Line[]>([]);
	const [note, setNote] = useState('');
	const [remarks, setRemarks] = useState('');
	const [vendorQuoteNo, setVendorQuoteNo] = useState('');
	const [expectedDelivery, setExpectedDelivery] = useState('');
	const [deliveryTerms, setDeliveryTerms] = useState('');
	const [locked, setLocked] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [savingDraft, setSavingDraft] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	useEffect(() => {
		(async () => {
			try {
				const data = await getQuoteLinkDetail(token as string);
				setVendor(data?.vendor || null);
				setReq(data?.requisition || null);
				setLocked(data?.isLocked === true);

				// Prefill from an existing draft/submitted quote if present.
				const quote = data?.quotation;
				const quotedByProduct: Record<string, any> = {};
				(quote?.products || []).forEach((qp: any) => {
					quotedByProduct[qp.productId || qp.productInfo?._id] = qp;
				});
				const reqProducts = data?.requisition?.products || [];
				setLines(reqProducts.map((p: any) => mapLine(p, quotedByProduct[p.productId])));

				if (quote) {
					setNote(quote.note || '');
					setRemarks(quote.remarks || '');
					setVendorQuoteNo(quote.vendor_quotation_no || '');
					setExpectedDelivery(
						quote.expected_delivery ? String(quote.expected_delivery).slice(0, 10) : '',
					);
					setDeliveryTerms(quote.delivery_terms || '');
				}
			} catch (err: any) {
				setError(err?.apiMessage || 'This quotation link is invalid or has expired.');
			} finally {
				setLoading(false);
			}
		})();
	}, [token]);

	const grandTotal = useMemo(() => lines.reduce((s, l) => s + lineTotal(l), 0), [lines]);

	const update = (idx: number, field: keyof Line, value: any) => {
		setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
	};

	const buildPayload = () => ({
		note,
		remarks,
		vendor_quotation_no: vendorQuoteNo || undefined,
		expected_delivery: expectedDelivery || undefined,
		delivery_terms: deliveryTerms || undefined,
		products: lines.map((l) => ({
			productId: l.productId,
			product_description: l.product_description,
			usage_note: l.usage_note,
			quantity: Number(l.quantity),
			rate: Number(l.rate) || 0,
			tax: Number(l.tax) || 0,
			discount: Number(l.discount) || 0,
			discountType: l.discountType,
			amount_with_tax: Number(lineTotal(l).toFixed(2)),
		})),
	});

	const handleSaveDraft = async () => {
		if (!lines.some((l) => l.rate !== '' && Number(l.rate) > 0)) {
			toast.warn('Enter a rate for at least one product before saving a draft.');
			return;
		}
		setSavingDraft(true);
		try {
			await saveQuoteLinkDraft(token as string, buildPayload());
			toast.success('Draft saved. You can reopen this link anytime to continue.');
		} catch (err: any) {
			toast.error(err?.apiMessage || 'Could not save the draft.');
			if (err?.data?.alreadySubmitted || /already submitted/i.test(err?.apiMessage || '')) {
				setLocked(true);
			}
		} finally {
			setSavingDraft(false);
		}
	};

	const validateForSubmit = () => {
		if (!lines.length) return 'This requisition has no products.';
		for (const l of lines) {
			if (l.rate === '' || Number(l.rate) <= 0) return `Enter a valid rate for "${l.name}".`;
		}
		return '';
	};

	const openConfirm = () => {
		const v = validateForSubmit();
		if (v) {
			toast.warn(v);
			return;
		}
		setConfirmOpen(true);
	};

	const doSubmit = async () => {
		setSubmitting(true);
		try {
			await submitQuoteLink(token as string, buildPayload());
			setConfirmOpen(false);
			setLocked(true);
			toast.success('Quotation submitted. Thank you!');
		} catch (err: any) {
			setConfirmOpen(false);
			if (err?.data?.alreadySubmitted || /already submitted/i.test(err?.apiMessage || '')) {
				setLocked(true);
			}
			toast.error(err?.apiMessage || 'Could not submit the quotation.');
		} finally {
			setSubmitting(false);
		}
	};

	if (loading)
		return (
			<div className='vp' style={{ minHeight: '100vh' }}>
				<VpSpinner />
			</div>
		);

	if (error)
		return (
			<div className='vp' style={{ minHeight: '100vh', padding: 24 }}>
				<div className='vp-card vp-card-pad' style={{ maxWidth: 520, margin: '60px auto' }}>
					<div className='vp-alert vp-alert-error'>{error}</div>
				</div>
			</div>
		);

	return (
		<div className='vp' style={{ minHeight: '100vh', background: 'var(--vp-bg, #f4f6fb)' }}>
			<div style={{ maxWidth: 900, margin: '0 auto', padding: '18px 14px 40px' }}>
				{/* Brand header */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 12,
						padding: '10px 4px 18px',
					}}>
					<img src={neelsLogo} alt="Neel's Sanjayraj" style={{ height: 40 }} />
					<span className='vp-badge vp-badge-neutral'>Vendor Quotation</span>
				</div>

				{/* Vendor + requisition details */}
				<div className='vp-card vp-card-pad' style={{ marginBottom: 18 }}>
					<div className='vp-row-between' style={{ flexWrap: 'wrap', gap: 12 }}>
						<div>
							<div className='vp-muted' style={{ fontSize: 13 }}>
								Vendor
							</div>
							<div style={{ fontSize: 20, fontWeight: 700 }}>
								{vendor?.name || '—'}
							</div>
							<div className='vp-muted' style={{ fontSize: 13, marginTop: 4 }}>
								{vendor?.contact_no ? `📞 ${vendor.contact_no}` : ''}
								{vendor?.email ? `  ·  ✉️ ${vendor.email}` : ''}
							</div>
							{vendor?.gst && (
								<div className='vp-muted' style={{ fontSize: 12, marginTop: 2 }}>
									GST: {vendor.gst}
								</div>
							)}
						</div>
						<div style={{ textAlign: 'right' }}>
							<div className='vp-muted' style={{ fontSize: 13 }}>
								Requisition
							</div>
							<div style={{ fontSize: 18, fontWeight: 700 }}>
								{req?.requisition_no || '—'}
							</div>
							{locked ? (
								<span className='vp-badge vp-badge-confirmed' style={{ marginTop: 6 }}>
									Submitted
								</span>
							) : (
								<span className='vp-badge vp-badge-pending' style={{ marginTop: 6 }}>
									Awaiting your quotation
								</span>
							)}
						</div>
					</div>
				</div>

				{/* Already submitted banner */}
				{locked && (
					<div className='vp-congrats' style={{ marginBottom: 18 }}>
						<div className='vp-congrats-emoji'>
							<VpIcon name='accepted' size={40} />
						</div>
						<div>
							<h3>You have already submitted this quotation. Thank you!</h3>
							<p>Your quotation is locked and can no longer be edited.</p>
						</div>
					</div>
				)}

				{/* Products */}
				<div className='vp-card'>
					<div className='vp-card-head'>
						<h3>{locked ? 'Your Submitted Prices' : 'Enter Your Prices'}</h3>
					</div>
					<div className='vp-table-wrap'>
						<table className='vp-table'>
							<thead>
								<tr>
									<th>Product</th>
									<th>Qty</th>
									<th>Rate</th>
									<th>Discount</th>
									<th>Tax %</th>
									<th style={{ textAlign: 'right' }}>Line Total</th>
								</tr>
							</thead>
							<tbody>
								{lines.map((l, idx) => (
									<tr key={l.productId + idx}>
										<td>
											<div style={{ fontWeight: 600 }}>{l.name}</div>
											{l.product_description && (
												<div className='vp-muted' style={{ fontSize: 12 }}>
													{l.product_description}
												</div>
											)}
											{l.usage_note && (
												<div
													className='vp-muted'
													style={{ fontSize: 12, marginTop: 2 }}>
													<strong>Usage / Used For:</strong>{' '}
													{l.usage_note}
												</div>
											)}
										</td>
										<td>
											{l.quantity} {l.unit}
										</td>
										<td>
											{locked ? (
												money(Number(l.rate) || 0)
											) : (
												<input
													className='vp-input vp-input-sm'
													style={{ width: 100 }}
													type='number'
													min={0}
													value={l.rate}
													onChange={(e) =>
														update(idx, 'rate', e.target.value)
													}
													placeholder='0.00'
												/>
											)}
										</td>
										<td>
											{locked ? (
												`${l.discount || 0}${
													l.discountType === 'fixed' ? ' ₹' : ' %'
												}`
											) : (
												<div style={{ display: 'flex', gap: 6 }}>
													<input
														className='vp-input vp-input-sm'
														style={{ width: 72 }}
														type='number'
														min={0}
														value={l.discount}
														onChange={(e) =>
															update(idx, 'discount', e.target.value)
														}
														placeholder='0'
													/>
													<select
														className='vp-input vp-input-sm'
														style={{ width: 64 }}
														value={l.discountType}
														onChange={(e) =>
															update(
																idx,
																'discountType',
																e.target.value,
															)
														}>
														<option value='percentage'>%</option>
														<option value='fixed'>₹</option>
													</select>
												</div>
											)}
										</td>
										<td>
											{locked ? (
												`${l.tax || 0}%`
											) : (
												<input
													className='vp-input vp-input-sm'
													style={{ width: 72 }}
													type='number'
													min={0}
													value={l.tax}
													onChange={(e) =>
														update(idx, 'tax', e.target.value)
													}
													placeholder='0'
												/>
											)}
										</td>
										<td style={{ textAlign: 'right', fontWeight: 600 }}>
											{money(lineTotal(l))}
										</td>
									</tr>
								))}
							</tbody>
							<tfoot>
								<tr>
									<td
										colSpan={5}
										style={{ textAlign: 'right', fontWeight: 700 }}>
										Grand Total
									</td>
									<td
										style={{
											textAlign: 'right',
											fontWeight: 800,
											fontSize: 16,
										}}>
										{money(grandTotal)}
									</td>
								</tr>
							</tfoot>
						</table>
					</div>
				</div>

				{/* Delivery & notes */}
				<div className='vp-card vp-card-pad vp-mt-16'>
					<div className='vp-grid-2'>
						<div>
							<div className='vp-field'>
								<label className='vp-label'>Note (optional)</label>
								<input
									className='vp-input'
									value={note}
									disabled={locked}
									onChange={(e) => setNote(e.target.value)}
									placeholder='Any note for the buyer'
								/>
							</div>
							<div className='vp-field vp-mb-0'>
								<label className='vp-label'>Remarks (optional)</label>
								<input
									className='vp-input'
									value={remarks}
									disabled={locked}
									onChange={(e) => setRemarks(e.target.value)}
									placeholder='Validity, warranty, etc.'
								/>
							</div>
						</div>
						<div>
							<div className='vp-field'>
								<label className='vp-label'>
									Your Quotation Ref No. (optional)
								</label>
								<input
									className='vp-input'
									value={vendorQuoteNo}
									disabled={locked}
									onChange={(e) => setVendorQuoteNo(e.target.value)}
									placeholder='e.g. VQ-2024-001'
								/>
							</div>
							<div className='vp-field'>
								<label className='vp-label'>
									Expected Delivery Date (optional)
								</label>
								<input
									className='vp-input'
									type='date'
									value={expectedDelivery}
									disabled={locked}
									onChange={(e) => setExpectedDelivery(e.target.value)}
								/>
							</div>
							<div className='vp-field vp-mb-0'>
								<label className='vp-label'>Delivery Terms (optional)</label>
								<input
									className='vp-input'
									value={deliveryTerms}
									disabled={locked}
									onChange={(e) => setDeliveryTerms(e.target.value)}
									placeholder='e.g. FOB, ex-works, 7 days'
								/>
							</div>
						</div>
					</div>

					{!locked && (
						<>
							<div
								className='vp-muted vp-mt-16'
								style={{
									fontSize: 13,
									display: 'flex',
									gap: 6,
									alignItems: 'center',
								}}>
								<VpIcon name='warning' size={15} /> Save as draft to continue later,
								or Lock &amp; Submit to send your final quotation. After you submit,
								it is locked and cannot be changed.
							</div>
							<div
								className='vp-row-between vp-mt-16'
								style={{ flexWrap: 'wrap', gap: 10 }}>
								<button
									type='button'
									className='vp-btn vp-btn-ghost'
									disabled={savingDraft}
									onClick={handleSaveDraft}>
									{savingDraft ? 'Saving…' : 'Save as Draft'}
								</button>
								<button
									type='button'
									className='vp-btn vp-btn-primary'
									onClick={openConfirm}>
									Lock &amp; Submit
								</button>
							</div>
						</>
					)}
				</div>
			</div>

			<VpConfirmModal
				open={confirmOpen}
				icon='quotations'
				title='Lock &amp; submit this quotation?'
				message='After you submit, your quotation is locked and cannot be changed. Please review your prices before confirming.'
				confirmText='Yes, submit'
				cancelText='Review again'
				loading={submitting}
				onConfirm={doSubmit}
				onCancel={() => setConfirmOpen(false)}
			/>
		</div>
	);
};

export default QuoteLinkPage;
